import Vue from "vue";
import VueRouter from "vue-router";
import VueResource from "vue-resource";
import AsyncComputed from "vue-async-computed";
import VueTheMask from "vue-the-mask";
import VeeValidate from "vee-validate";
import VueAnalytics from "vue-analytics";
import VueNumerals from "vue-numerals";
import VueFuse from "vue-fuse";
import { datadogRum } from "@datadog/browser-rum";

import CKEditor from "@ckeditor/ckeditor5-vue";
import numeral from "numeral";

import routes from "./routes/routes.js";
import store from "./store/store.js";
import GlobalComponents from "./globalComponents.js";
import dictionary from "./components/Validators/dictionary.js";
import moment from "moment";
import "moment/locale/pt-br";
import * as Sentry from "@sentry/browser";
import { Integrations } from "@sentry/tracing";

import App from "./App.vue";

import AuthService from "./domain/web/AuthService";

if (process.env.NODE_ENV === "production") {
  datadogRum.init({
    applicationId: "4ea5e786-3502-4759-999d-3e9cf4496578",
    clientToken: "pub3d07ad2497a1a983a322e3b33d346542",
    site: "datadoghq.com",
    service: "interno-vue",
    env: "prod",
    // Specify a version number to identify the deployed version of your application in Datadog
    version: "latest",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 5,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input"
  });

  datadogRum.startSessionReplayRecording();
}

Vue.use(VueResource);
Vue.use(AsyncComputed);
Vue.use(VueTheMask);
Vue.use(GlobalComponents);
Vue.use(VueRouter);
Vue.use(VueFuse);
Vue.use(CKEditor);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== "NavigationDuplicated") throw err;
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== "NavigationDuplicated") throw err;
  });
};

Vue.use(require("vue-moment"), {
  moment
});

Vue.use(VueNumerals, {
  locale: "pt-br"
});

numeral.register("locale", "pt-BR", {
  delimiters: {
    thousands: ".",
    decimal: ","
  },
  currency: {
    symbol: "R$"
  }
});

numeral.locale("pt-BR");

Vue.moment.locale("pt-br");

const config = {
  dictionary,
  inject: false,
  locale: "pt_BR"
};

Vue.use(VeeValidate, config);

Vue.config.productionTip = false;

Vue.http.options.root = process.env.API_URL;

Vue.http.interceptors.push((request, next) => {
  const toRemove = [];
  const usuario = store.state.usuario;
  let removeAuthHeaders = false;

  toRemove.map(remove => {
    if (request.url.includes(remove)) {
      removeAuthHeaders = true;
    }
  });

  request.headers.set("Accept", "application/json");
  const token = store.state.token;
  let type = "Authorization";

  if (removeAuthHeaders) {
    request.headers.delete(type);
  } else {
    request.headers.set(type, token);
  }

  request.headers.set("Authorization", token);

  if (usuario && usuario.id) {
    Sentry.setUser({
      id: usuario.id,
      login: usuario.login,
      username: usuario.nome
    });
  }

  Sentry.setContext("request", {
    url: request.url,
    method: request.method
  });

  next(res => {
    if (
      (res.status === 401 || res.status === 0) &&
      res.url.indexOf("check-refresh") === -1
    ) {
      if (res.status === 0) {
        return Vue.http(request).then(data => data);
      }

      if (!store.state.lembrar) {
        return router.push("/logoff");
      }
      const authService = new AuthService(Vue.http);

      return authService.checkToken().then(
        result => {
          if (!result || !result.token) {
            return router.push("/logoff");
          }

          const token = `Bearer ${result.token}`;
          request.headers.set("Authorization", token);

          return Vue.http(request).then(data => data);
        },
        () => {
          return router.push("/logoff");
        }
      );
    }

    if (res.status >= 400) {
      const rota = new URL(res.url).pathname.replace(/\/\d+/g, "");

      Sentry.withScope(scope => {
        scope.setContext("api_error", {
          status: res.status,
          url: res.url,
          method: request.method,
          data: res.data,
          body: request.body
        });

        Sentry.captureMessage(`${request.method} - ${res.status} - ${rota}`);
      });
    }
  });
});

if (window.location.pathname.includes("//")) {
  const cleanPath = window.location.pathname.replace(/\/+/g, "/");
  window.history.replaceState(
    window.history.state,
    "",
    cleanPath + window.location.search + window.location.hash
  );
}

const router = new VueRouter({
  mode: "history",
  routes: routes,
  linkActiveClass: "active",
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({ x: 0, y: 0 });
      }, 500);
    });
  }
});

function nextFactory(context, middleware, index) {
  const subsequentMiddleware = middleware[index];
  if (!subsequentMiddleware) return context.next;

  return (...parameters) => {
    context.next(...parameters);
    const nextMiddleware = nextFactory(context, middleware, index + 1);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
}

router.beforeEach((to, from, next) => {
  if (to.path.includes("//")) {
    const cleanPath = to.path.replace(/\/+/g, "/");
    return next({
      path: cleanPath,
      query: to.query,
      hash: to.hash,
      replace: true
    });
  }
  if (to.meta.middleware) {
    const middleware = Array.isArray(to.meta.middleware)
      ? to.meta.middleware
      : [to.meta.middleware];

    store.commit("initialiseStore");
    const context = {
      from,
      next,
      router,
      to,
      store
    };
    const nextMiddleware = nextFactory(context, middleware, 1);

    return middleware[0]({ ...context, next: nextMiddleware });
  }

  return next();
});

Vue.use(VueAnalytics, {
  id: "UA-4934466-30",
  router
});

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    Vue: Vue,
    dsn:
      "https://aef6ad7e895d4df598191d840d69e190@o135686.ingest.sentry.io/1259477",
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.7,
    ignoreErrors: ["ResizeObserver loop", "Large Render Blocking Asset"]
  });
  Vue.config.errorHandler = error => {
    Sentry.captureException(error);
  };
}

new Vue({
  router,
  store,
  el: "#app",
  beforeCreate() {
    this.$store.commit("initialiseStore");
  },
  render: h => h(App)
});



// WEBPACK FOOTER //
// ./src/main.js